/**
 * Las piezas comunes de las reglas sobre las respuestas de los contactos
 * (31/10/2026): el orden de Pendientes por niveles
 * (components/PrioridadContactosDialog.jsx) y la exclusión al entrar
 * (components/ExclusionContactosDialog.jsx). Las reglas son DATOS de la
 * clínica (las guarda el servidor, services/prioridad_leads.py y
 * services/exclusion_leads.py): aquí no hay ninguna escrita.
 *
 *  · Una regla dice para qué procedencia vale (una campaña de tratamiento, o
 *    «Todas las procedencias», «*») y sus condiciones: se cumple si se
 *    cumplen TODAS.
 *  · Una condición es un dato del lead (plazo, presupuesto, plataforma…) y
 *    los valores que valen: cualquiera de los marcados. «(sin respuesta)» vale
 *    por «no lo dice» (su formulario no lo pregunta, o es de la web, del
 *    correo o puesto a mano).
 *
 * Los valores se ELIGEN de los que ya han dado los contactos de esa
 * procedencia (con cuántos lo han dado) y de las listas de Configuración: no
 * se teclean.
 */
import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2 } from 'lucide-react';

export const TODAS = '*';

export function mensajeError(e, porDefecto) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return porDefecto;
}

export const copiaRegla = (r) => ({
  ...r, condiciones: (r.condiciones || []).map((c) => ({ ...c, valores: [...(c.valores || [])] })),
});
export const copiaReglas = (reglas) => (reglas || []).map(copiaRegla);

/** Lo que impide guardar unas reglas, en cristiano; '' si se puede. */
export function problemaDeLasReglas(reglas, de) {
  for (let i = 0; i < reglas.length; i += 1) {
    const r = reglas[i];
    const quien = `${de}${de ? 'regla' : 'Regla'} ${i + 1}`;
    if (!r.condiciones.length) return `${quien}: añade al menos una condición.`;
    if (r.condiciones.some((c) => !c.valores.length)) return `${quien}: marca al menos un valor en cada condición.`;
  }
  return '';
}

/** Una regla nueva: para todas las procedencias, con el primer dato. */
export function reglaNueva(campos, procedencia = TODAS) {
  return { procedencia, condiciones: [{ campo: campos[0]?.clave || '', valores: [] }] };
}

/**
 * Una regla: para qué procedencia vale, sus condiciones (todas) y, si
 * `conMotivo`, el motivo que se lee en «Descartados».
 */
export function EditorRegla({
  regla, campos, procedencias, onChange, onQuitar, base, titulo, conMotivo = false,
}) {
  const proc = procedencias.find((p) => p.clave === regla.procedencia);
  const cambiar = (fn) => { const nueva = copiaRegla(regla); fn(nueva); onChange(nueva); };
  const nombreCampo = (clave) => campos.find((c) => c.clave === clave)?.nombre || clave;
  const usados = regla.condiciones.map((x) => x.campo);
  return (
    <div className="rounded border bg-muted/30 p-2 space-y-2" data-testid={base}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold">{titulo}</span>
        <select value={regla.procedencia} data-testid={`${base}-procedencia`}
          onChange={(e) => cambiar((r) => { r.procedencia = e.target.value; })}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm">
          {procedencias.map((p) => (
            <option key={p.clave} value={p.clave}>
              {p.nombre}{p.con_campana === false ? ' (ya no tiene campaña)' : ''}
            </option>
          ))}
          {!proc ? <option value={regla.procedencia}>{regla.procedencia}</option> : null}
        </select>
        <span className="text-xs text-muted-foreground">se tienen que cumplir todas:</span>
        <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={onQuitar}
          data-testid={`${base}-quitar`} title="Quitar esta regla">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {regla.condiciones.map((c, j) => {
        const ofrecidos = proc?.valores?.[c.campo] || [];
        const extra = c.valores.filter((v) => !ofrecidos.some((o) => o.valor === v))
          .map((v) => ({ valor: v, nombre: v, n: 0 }));
        const todos = [...ofrecidos, ...extra];
        const cbase = `${base}-condicion-${j}`;
        return (
          <div key={c.campo || j} className="space-y-1" data-testid={cbase}>
            <div className="flex flex-wrap items-center gap-2">
              {j > 0 ? <span className="text-xs text-muted-foreground">y</span> : null}
              <select value={c.campo} data-testid={`${cbase}-campo`}
                onChange={(e) => cambiar((r) => { r.condiciones[j] = { campo: e.target.value, valores: [] }; })}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm">
                {campos.filter((x) => x.clave === c.campo || !usados.includes(x.clave)).map((x) => (
                  <option key={x.clave} value={x.clave}>{x.nombre}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">es cualquiera de:</span>
              <Button type="button" size="sm" variant="ghost" className="ml-auto h-7"
                onClick={() => cambiar((r) => { r.condiciones.splice(j, 1); })} data-testid={`${cbase}-quitar`}>
                Quitar
              </Button>
            </div>
            {todos.length ? (
              <div className="flex flex-wrap gap-x-3 gap-y-1 pl-1">
                {todos.map((o, k) => (
                  <label key={o.valor} className="inline-flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={c.valores.includes(o.valor)}
                      data-testid={`${cbase}-valor-${k}`}
                      onChange={() => cambiar((r) => {
                        const x = r.condiciones[j];
                        x.valores = x.valores.includes(o.valor)
                          ? x.valores.filter((v) => v !== o.valor) : [...x.valores, o.valor];
                      })} />
                    <span>{o.nombre}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">({o.n})</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground pl-1">
                Ningún contacto ha contestado «{nombreCampo(c.campo)}» todavía.
              </p>
            )}
          </div>
        );
      })}
      {regla.condiciones.length < campos.length ? (
        <Button type="button" size="sm" variant="ghost" data-testid={`${base}-anadir-condicion`}
          onClick={() => cambiar((r) => {
            const libre = campos.find((x) => !r.condiciones.some((y) => y.campo === x.clave));
            if (libre) r.condiciones.push({ campo: libre.clave, valores: [] });
          })}>
          <Plus className="w-4 h-4 mr-1" /> Y además…
        </Button>
      ) : null}
      {conMotivo ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Motivo en «Descartados»:</span>
          <Input value={regla.motivo || ''} maxLength={200} className="h-8 max-w-md text-sm"
            data-testid={`${base}-motivo`} placeholder="Si lo dejas vacío, dirá lo que cumple"
            onChange={(e) => cambiar((r) => { r.motivo = e.target.value; })} />
        </div>
      ) : null}
    </div>
  );
}
